import { Observable, of } from 'rxjs';
import { Injectable, makeStateKey, TransferState, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, switchMap } from 'rxjs/operators';
import { IPostModel } from '@app/pages/posts/models/post.model';
import { TServiceResult } from '@shared/helper/t-service-result';
import { LocalRepository } from '@shared/helper/local-repository';
import { PageList } from '@shared/model/categoryAndSearch/pageList-model';
import { HttpBaseService } from '@shared/service/base_services/http_base.service';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { AccessValidationModel } from '@shared/model/customerModels/userAccess-model';


const STATE_KEY_LATEST_WEEK_POSTS_HOME = makeStateKey<TServiceResult<PageList<IPostModel>>>('latest_week_posts_home');
const STATE_KEY_CHOSEN_POSTS_HOME = makeStateKey<TServiceResult<PageList<IPostModel>>>('chosen_posts_home');
const STATE_KEY_CHOSEN_POSTS_FEEDS = makeStateKey<TServiceResult<PageList<IPostModel>>>('chosen_posts_feeds');


@Injectable({
    providedIn: 'root'
})
export class PostAggService extends HttpBaseService {
    private serverState = inject(TransferState);


    constructor() {
        const repo = inject(LocalRepository);
        const http = inject(HttpClient);

        super(repo, http, 'v1/poAggr');
    }

    getPost(postId: string): Observable<TServiceResult<IPostModel>> {
        return this.http.get<TServiceResult<IPostModel>>(`${this.apiUrl}/GetPost/${postId}`);
    }

    getGuestPosts(guestId: string, termFilter?: TermFilterModel, exceptPostId?: string): Observable<TServiceResult<PageList<IPostModel>>> {
        const paramsValues = this.TermFilterParams(termFilter);
        return this.http.get<TServiceResult<PageList<IPostModel>>>(`${this.apiUrl}/GetGuestPosts/${guestId}${paramsValues}&exceptPostId=${exceptPostId ?? null}`)
            .pipe(
                catchError(err => {
                    return of(new TServiceResult<PageList<IPostModel>>(new PageList(), '', null, true, ''))
                })
            )
    }

    getSuggestedPosts(postId: string, termFilter?: TermFilterModel): Observable<TServiceResult<PageList<IPostModel>>> {
        const paramsValues = this.TermFilterParams(termFilter)
        return this.http.get<TServiceResult<PageList<IPostModel>>>(`${this.apiUrl}/GetSuggestedPosts/${postId}${paramsValues}`)
    }

    getLatestPosts(termFilter?: TermFilterModel): Observable<TServiceResult<PageList<IPostModel>>> {
        const paramsValues = this.TermFilterParams(termFilter)
        return this.http.get<TServiceResult<PageList<IPostModel>>>(`${this.apiUrl}/GetLatestPosts?${paramsValues}`)
    }

    getLatestPostsWithUserLiked(termFilter?: TermFilterModel): Observable<TServiceResult<PageList<IPostModel>>> {
        const paramsValues = this.TermFilterParams(termFilter)
        return this.http.get<TServiceResult<PageList<IPostModel>>>(`${this.apiUrl}/GetLatestPostsWithUserLiked?${paramsValues}`)
    }

    getLatestWeekPosts(termFilter?: TermFilterModel): Observable<TServiceResult<PageList<IPostModel>>> {
        const paramsValues = this.TermFilterParams(termFilter)
        return this.http.get<TServiceResult<PageList<IPostModel>>>(`${this.apiUrl}/GetLatestWeekPosts?${paramsValues}`)
            .pipe(
                switchMap(res => {
                    this.serverState.set<TServiceResult<PageList<IPostModel>>>(STATE_KEY_LATEST_WEEK_POSTS_HOME, res);
                    return of(res);
                })
            )
    }

    getLatestWeekPostsWithUserLiked(termFilter?: TermFilterModel): Observable<TServiceResult<PageList<IPostModel>>> {
        const paramsValues = this.TermFilterParams(termFilter)
        return this.http.get<TServiceResult<PageList<IPostModel>>>(`${this.apiUrl}/GetLatestWeekPostsWithUserLiked?${paramsValues}`)
    }

    getSuggestedPostsByBookId(bookId: string, termFilter?: TermFilterModel): Observable<TServiceResult<PageList<IPostModel>>> {
        const paramsValues = this.TermFilterParams(termFilter);
        return this.http.get<TServiceResult<PageList<IPostModel>>>(`${this.apiUrl}/GetSuggestedPostsByBookId/${bookId}${paramsValues}`);
    }

    getChosenBookLists(termFilter: TermFilterModel, tag = 'feed'): Observable<TServiceResult<PageList<IPostModel>>> {
        const paramsValues = this.ServiceTermFilter(termFilter);
        return this.http.get<TServiceResult<PageList<IPostModel>>>(`${this.apiUrl}/GetChosenBookLists/${paramsValues}&tag=${tag}`)
            .pipe(
                switchMap(res => {
                    this.serverState.set<TServiceResult<PageList<IPostModel>>>(STATE_KEY_CHOSEN_POSTS_HOME, res);
                    return of(res)
                }),
                catchError(() => {
                    return of()

                })
            )
    }

    getChosenBookListsWithUserLiked(termFilter: TermFilterModel, tag = 'feed'): Observable<TServiceResult<PageList<IPostModel>>> {
        const paramsValues = this.ServiceTermFilter(termFilter);
        return this.http.get<TServiceResult<PageList<IPostModel>>>(`${this.apiUrl}/GetChosenBookListsWithUserLiked/${paramsValues}&tag=${tag}`)
            .pipe(
                switchMap(res => {
                    this.serverState.set<TServiceResult<PageList<IPostModel>>>(STATE_KEY_CHOSEN_POSTS_FEEDS, res)
                    return of(res)
                })
            )
    }

    bloggerShipValidation(): Observable<TServiceResult<AccessValidationModel>> {
        return this.http.post<TServiceResult<AccessValidationModel>>(`${this.apiUrl}/BloggershipValidation`, null)
    }
}

